'use client';

import { useState } from 'react';
import classNames from 'classnames';
import ShoppingCartItemRateDetails from './ShoppingCartItemRateDetails';
import { useTranslations } from '@fshr/context/i18n-context';
import { formatPrice } from '@fshr/shared';
import { IconCollapse, IconCollapseUp, IconInformation } from '../../icon';
import Image from '../../image';
import Button from '../../button';
import { Oj } from '@fshr/shared/types';
import BevSafeText from '../../bev-safe-text';

interface ShoppingCartItemProps {
	currencyCode?: string;
	name?: string;
	cartDescription?: string;
	roomRate?: Oj.ProductRoomRate;
	numberOfPeople?: string;
	duration?: string;
	price?: number;
	stayResortFees?: Oj.StayResortFees;
	taxAndFees?: string;
	quantity?: number;
	removeItem?: () => void;
	showInfoModal?: () => void;
	insideCart?: boolean;
	productType?: string;
	image?: string;
	onRequestEventProductType?: string;
	unitType?: string;
	complimentaryPricingInfo?: string;
	requestDate?: string;
	// Additional properties for ancillaries
	isAncillary?: boolean;
	timeOfDay?: string;
	ancillaryDisclaimer?: string;
	isOutsideDates?: boolean;
	isMpeJourney?: boolean;
}

export default function ShoppingCartItem({
	currencyCode,
	name,
	cartDescription,
	roomRate,
	numberOfPeople,
	duration,
	quantity,
	price,
	stayResortFees,
	taxAndFees = 'Taxes & fees may apply',
	removeItem,
	showInfoModal,
	insideCart = false,
	image = '',
	unitType,
	complimentaryPricingInfo,
	requestDate,
	isAncillary = false,
	isOutsideDates = false,
	isMpeJourney = false,
}: ShoppingCartItemProps) {
	const t = useTranslations();

	const [showRoomRateDetails, setShowRoomRateDetails] = useState(true);

	return (
		<>
			<div className="flex flex-row items-start spacing-inline-s">
				{image && (
					<Image
						src={image}
						alt={name || 'Product image'}
						className={classNames('object-cover', {
							'w-[135px] h-[135px] min-w-[135px] min-h-[135px]': !isAncillary && !isOutsideDates,
							'w-[120px] h-[120px] min-w-[120px] min-h-[120px]': isAncillary,
							'w-[100px] h-[100px] min-w-[100px] min-h-[100px]': isOutsideDates,
						})}
						roughSize="s"
					/>
				)}
				<div
					className={classNames('w-full justify-end md:justify-between md:flex spacing-inline-s', {
						'min-h-[135px]': !isAncillary && !isOutsideDates,
						'min-h-[120px]': isAncillary,
						'min-h-[100px]': isOutsideDates,
					})}
				>
					<div className="flex flex-col md:spacing-stack-xs mb-100 md:mb-0">
						<div className="flex flex-col items-start justify-start">
							<span className="text-subtitle2">{name}</span>
							{cartDescription && (
								<span className="text-caption">
									<BevSafeText>{cartDescription}</BevSafeText>
								</span>
							)}
							{(duration || numberOfPeople) && (
								<span className="text-caption">
									{[duration, numberOfPeople].filter(Boolean).join(' | ')}
								</span>
							)}
							{quantity && <span className="text-caption">{t('Quantity: {0}', [quantity])}</span>}
							{requestDate && <span className="text-caption">{requestDate}</span>}
						</div>
						<div>
							{removeItem && !isOutsideDates && (
								<Button
									onClick={e => {
										e.stopPropagation();
										removeItem();
									}}
									className="text-[#666666] border-b border-b-[#666666] text-caption capitalize hover:border-b-[#767676] cursor-pointer"
									intent="unstyled"
								>
									{t('Remove')}
								</Button>
							)}
						</div>
					</div>
					<div className="flex flex-col justify-between items-end">
						<div className="flex flex-col items-end justify-start w-auto">
							<div className="flex flex-row spacing-inline-2xs">
								{complimentaryPricingInfo ? (
									<span className="text-subtitle2 text-right">{complimentaryPricingInfo}</span>
								) : (
									<>
										{showInfoModal && unitType !== 'Price per unit' && (
											<Button
												onClick={e => {
													e.stopPropagation();
													showInfoModal();
												}}
												intent="unstyled"
											>
												<IconInformation fill="black" width={12} height={12} />
											</Button>
										)}
										<span className="text-subtitle2 text-right">
											{price && currencyCode
												? String(formatPrice({ value: price, currencyCode: currencyCode }))
												: `${price} ${currencyCode}`}
										</span>
									</>
								)}
							</div>
							<span
								className="text-caption text-right"
								data-cy="shopping-cart-item__taxes-and-fees"
							>
								{isMpeJourney ? t('Before taxes') : t(taxAndFees)}
							</span>
						</div>
						{removeItem && isOutsideDates && (
							<div>
								<Button
									intent="outline"
									className="w-[125px] max-h-[30px]"
									onClick={e => {
										e.stopPropagation();
										removeItem();
									}}
								>
									{t('Remove')}
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
			{/* Show rate details toggle for hotel products */}
			{!insideCart && roomRate && !isMpeJourney && (
				<>
					<Button
						type="button"
						onClick={() => {
							setShowRoomRateDetails(prev => !prev);
						}}
						className="flex flex-row justify-end items-center spacing-inline-2xs"
						intent="unstyled"
					>
						<div className="text-label">
							{showRoomRateDetails ? t('Hide Rate Details') : t('View Rate Details')}
						</div>
						{showRoomRateDetails ? <IconCollapse /> : <IconCollapseUp />}
					</Button>
					{showRoomRateDetails && (
						<ShoppingCartItemRateDetails
							roomRate={roomRate}
							currencyCode={currencyCode || ''}
							stayResortFees={stayResortFees || []}
							taxAndFees={taxAndFees}
						/>
					)}
				</>
			)}
		</>
	);
}
